import React, { useContext, useMemo } from 'react';
import { array, bool, string, number, object, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';

import ContextStore from 'utils/createContext';

import { PlayerPlayIcon, PlayerPauseIcon } from 'components/Icon';
import LineProgressBar from 'components/LineProgressBar';
import MaterialDropdownButton from 'components/MaterialDropdownButton';
import SaveLessonButton from 'routes/live/components/SaveLessonButton';

import getDateTimeWithStatus from 'routes/live/utils/getDateTimeWithStatus';
import useSaveLesson from 'routes/live/hooks/useSaveLesson';

import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { getUserIsAuthenticated } from 'reduxModules/user/selector';
import { getLiveFilterInfo } from 'reduxModules/live/selector';

import { REGULAR_CLASS_MATERIAL_CLICK } from 'constants/gtmEvent';

import styles from './EpisodeListItem.scss';

function EpisodeListItem({
  expiredDays,
  expiredHours,
  id,
  isCompleted,
  isDisabled,
  // TODO: 靜待測試月租制
  // isFinished,
  isFirstUpcoming,
  isFree,
  isLive,
  isPlaying,
  isSaved,
  isSelected,
  learningProgress,
  liveTopicId,
  liveTopicName,
  materials,
  name,
  onClick,
  ordinalNumber,
  remainingDurationMinutes,
  startAt,
  status,
  time,
  isSemester,
  isRealLive,
  isWithoutVideoDuration,
  watchEnabled,
  watchExpiredAt,
  instructorNameList,
  instructorIdList,
}) {
  useStyles(styles);

  const { t } = useTranslation(['live']);
  const dispatch = useDispatch();

  const { viewMode } = useContext(ContextStore);
  const isAuthenticated = useSelector(getUserIsAuthenticated);
  const { selectedGradeLevel } = useSelector(getLiveFilterInfo);

  const { toggleSaveState, lessonIsSaved } = useSaveLesson(
    {
      id,
      name,
      liveTopicId,
      liveTopicName,
      startAt,
      status,
      ordinalNumber,
      watchEnabled,
      learningProgress,
    },
    isSaved,
  );

  const showProgressBar =
    !isFree && watchEnabled && !isRealLive && !isWithoutVideoDuration;
  const showMaterialButton = materials.length > 0;
  // in lesson the value of downloadable will be the same, so we take first material as represented
  const canDownloadMaterials = !!materials[0]?.downloadable;

  const dateTimeString = getDateTimeWithStatus(time, t);

  const expiredAtString = useMemo(() => {
    if (expiredDays >= 1)
      return t('topic.replayCountdown.day', {
        count: expiredDays,
      });

    return t('topic.replayCountdown.hour', {
      count: expiredHours >= 1 ? expiredHours : 1,
    });
  }, [expiredDays, expiredHours, t]);

  const progressString = useMemo(() => {
    if (isCompleted) return t('topic.replayStatus.completed');
    return t('topic.replayStatus.watching', {
      minute: remainingDurationMinutes,
    });
  }, [isCompleted, remainingDurationMinutes, t]);

  const renderPlayerAction = () => {
    if (isSelected && isPlaying) return <PlayerPauseIcon />;
    return <PlayerPlayIcon />;
  };

  const renderAction = () => {
    if (isFree) return renderPlayerAction();
    if (viewMode && !isAuthenticated) return null;

    return (
      <SaveLessonButton isSaved={lessonIsSaved} onClick={toggleSaveState} />
    );
  };

  const onMaterialClick = ({
    id: materialId,
    title,
    index: materialIndex,
  }) => () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_MATERIAL_CLICK,
        data: {
          topicId: liveTopicId,
          topicName: liveTopicName,
          gradeLevelName: selectedGradeLevel.name,
          instructorId: instructorIdList,
          instructorName: instructorNameList,
          materialId,
          materialName: title,
          itemIndex: materialIndex,
        },
      }),
    );
  };

  if (isDisabled) {
    return (
      <li role="menuitem" className={cx(styles.root, styles.isDisabled)}>
        <div className={styles.content}>
          <div className={styles.title}>{name}</div>
          <div className={styles.time}>{t('topic.noResourceHint')}</div>
        </div>
      </li>
    );
  }

  return (
    <li
      role="menuitem"
      className={cx(styles.root, {
        [styles.isFree]: isFree,
        [styles.isSelected]: isSelected,
      })}
      onClick={onClick}
      data-first-upcoming={isFirstUpcoming}
    >
      <div className={styles.content}>
        <div className={styles.title}>{name}</div>
        {((!isFree && !isSemester) || isLive || (isSemester && isRealLive)) && (
          <div className={cx(styles.time, { [styles.isLive]: isLive })}>
            {dateTimeString}
          </div>
        )}
        <div className={styles.collapsedContent}>
          {(watchExpiredAt || isSemester) && !isRealLive && !isFree && (
            <div className={styles.expiredAt}>{expiredAtString}</div>
          )}
          {(showProgressBar || showMaterialButton) && (
            <div className={styles.progressAndMaterial}>
              {showProgressBar && (
                <LineProgressBar
                  textStart={progressString}
                  progress={learningProgress}
                />
              )}
              <MaterialDropdownButton
                btnText={t('topic.materials')}
                isDisabled={!canDownloadMaterials}
                materials={materials}
                className={cx({
                  [styles.hideMaterial]: !showMaterialButton,
                })}
                onClickMaterial={onMaterialClick}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.action}>{renderAction()}</div>
    </li>
  );
}

EpisodeListItem.propTypes = {
  expiredDays: number.isRequired,
  expiredHours: number.isRequired,
  id: number.isRequired,
  instructorIdList: string.isRequired,
  instructorNameList: string.isRequired,
  isCompleted: bool.isRequired,
  isDisabled: bool.isRequired,
  // isFinished: bool.isRequired,
  isFirstUpcoming: bool.isRequired,
  isFree: bool.isRequired,
  isLive: bool.isRequired,
  isSaved: bool.isRequired,
  learningProgress: number.isRequired,
  liveTopicId: number.isRequired,
  liveTopicName: string.isRequired,
  materials: array.isRequired,
  name: string.isRequired,
  onClick: func.isRequired,
  ordinalNumber: number.isRequired,
  remainingDurationMinutes: number.isRequired,
  startAt: string.isRequired,
  status: string.isRequired,
  time: object.isRequired,
  watchEnabled: bool.isRequired,
  isPlaying: bool,
  isSelected: bool,
  isSemester: bool,
  isRealLive: bool,
  isWithoutVideoDuration: bool,
  watchExpiredAt: string,
};

EpisodeListItem.defaultProps = {
  isPlaying: false,
  isSelected: false,
  isSemester: false,
  isWithoutVideoDuration: false,
  isRealLive: false,
  watchExpiredAt: null,
};

export default EpisodeListItem;
